import { addDays } from 'date-fns';
import { format } from '@querodelivery/qd-date-helper';
import type { IEvent, ILocation, StatusDelivery } from '@/Interfaces/Data';
import { STATUS_ENTREGA } from '@/Constants';
import { isSameMapPosition } from '@/_components/map/utils';
import type { DELIVERY_ACTION_EVENT } from './constants';
import { DELIVERY_ACTION_EVENTS, GPS_ROUTE_RETENTION_DAYS } from './constants';
import type {
  DeliveryActionPin,
  DeliveryRouteData,
  DeliveryTimestamps,
  MapPosition,
  RouteActionTarget,
  RouteFocusTarget,
  RouteSequenceItem,
} from './types';

export const isValidLocation = (
  location?: ILocation | null,
): location is ILocation => {
  if (!location) {
    return false;
  }

  const latitude = Number(location.latitude);
  const longitude = Number(location.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return false;
  }

  return !(latitude === 0 && longitude === 0);
};

export const toMapPosition = (
  location?: ILocation | null,
): MapPosition | null => {
  if (!isValidLocation(location)) {
    return null;
  }

  return [Number(location.latitude), Number(location.longitude)];
};

export const toMapPositionFromUnknown = (
  location?: unknown,
): MapPosition | null => {
  if (!location || typeof location !== 'object') {
    return null;
  }

  const value = location as {
    latitude?: unknown;
    longitude?: unknown;
    coordinates?: unknown;
  };

  if (value.latitude != null && value.longitude != null) {
    return toMapPosition({
      latitude: Number(value.latitude),
      longitude: Number(value.longitude),
    });
  }

  if (!Array.isArray(value.coordinates) || value.coordinates.length < 2) {
    return null;
  }

  return toMapPosition({
    latitude: Number(value.coordinates[1]),
    longitude: Number(value.coordinates[0]),
  });
};

export const isManagerExecutedEvent = (evento?: string) =>
  Boolean(evento?.includes('_BY_MANAGER'));

const matchesActionEvent = (
  evento: string | undefined,
  events: DELIVERY_ACTION_EVENT[],
) => Boolean(evento && events.includes(evento as DELIVERY_ACTION_EVENT));

export const getActionExecutionById = (delivery: DeliveryRouteData) => {
  const events = Array.isArray(delivery.eventos)
    ? Array.from(delivery.eventos)
    : [];
  const executionById = new Map<
    RouteActionTarget,
    { executedByManager: boolean }
  >();

  DELIVERY_ACTION_EVENTS.forEach(action => {
    const lastEvent = events
      .filter(event => matchesActionEvent(event.evento, action.events))
      .at(-1);

    if (!lastEvent) {
      return;
    }

    executionById.set(action.id, {
      executedByManager: isManagerExecutedEvent(lastEvent.evento),
    });
  });

  return executionById;
};

export const getEventDeliverymanLocation = (
  event?: IEvent | null,
): MapPosition | null => {
  const payload = event?.payload;

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  return (
    toMapPositionFromUnknown(payload.entregador?.lastLocalizacao) ||
    toMapPositionFromUnknown(payload.lastLocalizacaoEntregador) ||
    toMapPositionFromUnknown(payload.lastLocalizacao)
  );
};

export const isDeliveryFinished = (status?: StatusDelivery) =>
  status === STATUS_ENTREGA.FINALIZADO || status === STATUS_ENTREGA.CANCELADO;

export const isDeliveryInProgress = (status?: StatusDelivery) =>
  Boolean(status) && !isDeliveryFinished(status);

export const getLiveDeliverymanLocation = (
  delivery: DeliveryRouteData,
): MapPosition | null => {
  if (!isDeliveryInProgress(delivery.status)) {
    return null;
  }

  return toMapPosition(delivery.deliverymanCurrentLocation);
};

export const isDeliveredWithLiveLocation = (delivery: DeliveryRouteData) =>
  delivery.status === STATUS_ENTREGA.ENTREGUE &&
  Boolean(getLiveDeliverymanLocation(delivery));

export const getLiveDeliverymanIntentTarget = (
  delivery: DeliveryRouteData,
  placeLocation: MapPosition | null,
  deliveryLocation: MapPosition | null,
): MapPosition | null => {
  if (!getLiveDeliverymanLocation(delivery)) {
    return null;
  }

  if (
    delivery.status === STATUS_ENTREGA.CHEGOU_NA_LOJA ||
    delivery.status === STATUS_ENTREGA.ENTREGUE
  ) {
    return null;
  }

  if (
    delivery.status === STATUS_ENTREGA.SAIU_DA_LOJA ||
    delivery.status === STATUS_ENTREGA.CHEGOU_NO_CLIENTE
  ) {
    return deliveryLocation;
  }

  return placeLocation;
};

export const shouldShowLiveIntentLine = ({
  liveLocation,
  intentTarget,
  hasGpsRoute,
}: {
  liveLocation: MapPosition | null;
  intentTarget: MapPosition | null;
  hasGpsRoute: boolean;
}) =>
  Boolean(
    liveLocation &&
      intentTarget &&
      !isSameMapPosition(liveLocation, intentTarget) &&
      !hasGpsRoute,
  );

const pickTimestamp = (
  ...values: Array<Date | string | undefined>
): Date | string | undefined => values.find(value => Boolean(value));

export const getDeliveryTimestamps = (
  delivery: DeliveryRouteData,
): DeliveryTimestamps => ({
  acceptedAt: pickTimestamp(delivery.acceptedInfo?.aceiteAt, delivery.aceiteAt),
  arrivedStoreAt: pickTimestamp(
    delivery.arrivedStoreInfo?.chegouLojaAt,
    delivery.chegouLojaAt,
  ),
  leftStoreAt: pickTimestamp(
    delivery.leftStoreInfo?.saiuLojaAt,
    delivery.saiuLojaAt,
  ),
  arrivedClientAt: pickTimestamp(
    delivery.arrivedClientInfo?.chegouClienteAt,
    delivery.chegouClienteAt,
  ),
  deliveredAt: pickTimestamp(
    delivery.deliveredInfo?.entregueAt,
    delivery.entregueAt,
  ),
  returnedAt: delivery.retornoLojaAt,
  finishedAt: pickTimestamp(
    delivery.finishedInfo?.finalizadoAt,
    delivery.finalizadoAt,
  ),
});

export const formatRouteTime = (value?: Date | string, pattern = 'HH:mm') => {
  if (!value) {
    return undefined;
  }

  try {
    return format(value, pattern);
  } catch {
    return undefined;
  }
};

export const formatRouteDateTime = (value?: Date | string) =>
  formatRouteTime(value, 'dd/MM HH:mm');

const hasGpsRouteOrLiveDelivery = (delivery: DeliveryRouteData) =>
  Boolean(delivery.encodedRoute) || isDeliveryInProgress(delivery.status);

export const getDeliveryActionPins = (
  delivery: DeliveryRouteData,
): DeliveryActionPin[] => {
  if (
    !hasGpsRouteOrLiveDelivery(delivery) &&
    isGpsRouteRetentionExpired(delivery.createdAt)
  ) {
    return [];
  }

  const events = Array.isArray(delivery.eventos)
    ? Array.from(delivery.eventos)
    : [];

  if (events.length === 0) {
    return [];
  }

  const executionById = getActionExecutionById(delivery);

  return DELIVERY_ACTION_EVENTS.reduce<DeliveryActionPin[]>((pins, action) => {
    const matchingEvents = events.filter(event =>
      matchesActionEvent(event.evento, action.events),
    );
    const eventWithLocation = [...matchingEvents]
      .reverse()
      .find(event => getEventDeliverymanLocation(event));
    const position = getEventDeliverymanLocation(eventWithLocation);

    if (!position) {
      return pins;
    }

    pins.push({
      id: action.id,
      label: action.label,
      position,
      time: formatRouteTime(eventWithLocation?.createdAt),
      index: pins.length + 1,
      executedByManager: Boolean(
        executionById.get(action.id)?.executedByManager,
      ),
    });

    return pins;
  }, []);
};

export const numberActionPinsBySequence = (
  pins: DeliveryActionPin[],
  sequence: RouteSequenceItem[],
): DeliveryActionPin[] => {
  const pinsById = new Map<string, DeliveryActionPin>(
    pins.map(pin => [pin.id, pin]),
  );

  return sequence.reduce<DeliveryActionPin[]>((numberedPins, item) => {
    if (item.symbol !== 'action') {
      return numberedPins;
    }

    const pin = pinsById.get(item.id);

    if (!pin) {
      return numberedPins;
    }

    numberedPins.push({
      ...pin,
      index: numberedPins.length + 1,
    });

    return numberedPins;
  }, []);
};

const isUsableAddress = (address?: string) =>
  Boolean(address) && address !== 'não disponível' && address !== 's/n';

export const getRouteEndpoints = (
  route: MapPosition[],
  placeLocation: MapPosition | null,
  deliveryLocation: MapPosition | null,
) => {
  const startPosition = route[0] ?? null;
  const endPosition = route[route.length - 1] ?? null;

  const showStart = Boolean(
    startPosition &&
      (!placeLocation || !isSameMapPosition(startPosition, placeLocation)) &&
      (!deliveryLocation ||
        !isSameMapPosition(startPosition, deliveryLocation)),
  );

  const showEnd = Boolean(
    endPosition &&
      (!deliveryLocation ||
        !isSameMapPosition(endPosition, deliveryLocation)) &&
      (!placeLocation || !isSameMapPosition(endPosition, placeLocation)) &&
      (!startPosition || !isSameMapPosition(endPosition, startPosition)),
  );

  return {
    startPosition,
    endPosition,
    showStart,
    showEnd,
  };
};

export const getDeliveryRouteSequence = ({
  delivery,
  placeAddress,
  deliveryAddress,
  hasPlace,
  hasDelivery,
  showStart,
  showEnd,
  actionTargets = new Set<RouteActionTarget>(),
}: {
  delivery: DeliveryRouteData;
  placeAddress?: string;
  deliveryAddress?: string;
  hasPlace: boolean;
  hasDelivery: boolean;
  showStart: boolean;
  showEnd: boolean;
  actionTargets?: Set<RouteActionTarget>;
}): RouteSequenceItem[] => {
  const timestamps = getDeliveryTimestamps(delivery);
  const executionById = getActionExecutionById(delivery);
  const items: RouteSequenceItem[] = [];
  const managerFields = (actionId: RouteActionTarget) =>
    executionById.get(actionId)?.executedByManager
      ? { executedByManager: true as const }
      : {};
  const firstTarget = (
    ...candidates: Array<RouteFocusTarget | false | undefined>
  ): RouteFocusTarget => {
    const match = candidates.find(
      (candidate): candidate is RouteFocusTarget =>
        typeof candidate === 'string',
    );

    return match ?? 'all';
  };
  const resolveTarget = (
    actionId: RouteActionTarget,
    fallback: RouteFocusTarget,
  ): RouteFocusTarget => (actionTargets.has(actionId) ? actionId : fallback);

  if (showStart) {
    items.push({
      id: 'start',
      title: 'Início do trajeto',
      time: formatRouteDateTime(timestamps.acceptedAt),
      target: 'start',
      symbol: 'start',
    });
  }

  if (timestamps.acceptedAt) {
    items.push({
      id: 'accepted',
      title: 'Aceitou a entrega',
      time: formatRouteDateTime(timestamps.acceptedAt),
      target: resolveTarget(
        'accepted',
        firstTarget(showStart && 'start', hasPlace && 'place'),
      ),
      symbol: 'action',
      ...managerFields('accepted'),
    });
  }

  if (hasPlace) {
    items.push({
      id: 'place',
      title: 'Loja',
      address: isUsableAddress(placeAddress)
        ? placeAddress
        : delivery.nomeEstabelecimento,
      time: formatRouteDateTime(timestamps.arrivedStoreAt),
      target: 'place',
      symbol: 'place',
    });
  }

  if (timestamps.arrivedStoreAt) {
    items.push({
      id: 'arrived-store',
      title: 'Chegou na loja',
      time: formatRouteDateTime(timestamps.arrivedStoreAt),
      target: resolveTarget('arrived-store', firstTarget(hasPlace && 'place')),
      symbol: 'action',
      ...managerFields('arrived-store'),
    });
  }

  if (timestamps.leftStoreAt) {
    items.push({
      id: 'left-store',
      title: 'Saiu da loja',
      time: formatRouteDateTime(timestamps.leftStoreAt),
      target: resolveTarget('left-store', firstTarget(hasPlace && 'place')),
      symbol: 'action',
      ...managerFields('left-store'),
    });
  }

  if (hasDelivery) {
    items.push({
      id: 'delivery',
      title: 'Entrega',
      address: isUsableAddress(deliveryAddress)
        ? deliveryAddress
        : delivery.nomeDestinatario,
      time: formatRouteDateTime(
        timestamps.arrivedClientAt || timestamps.deliveredAt,
      ),
      target: 'delivery',
      symbol: 'delivery',
    });
  }

  if (timestamps.arrivedClientAt) {
    items.push({
      id: 'arrived-client',
      title: 'Chegou no cliente',
      time: formatRouteDateTime(timestamps.arrivedClientAt),
      target: resolveTarget(
        'arrived-client',
        firstTarget(hasDelivery && 'delivery'),
      ),
      symbol: 'action',
      ...managerFields('arrived-client'),
    });
  }

  if (timestamps.deliveredAt) {
    items.push({
      id: 'delivered',
      title: 'Entregou',
      time: formatRouteDateTime(timestamps.deliveredAt),
      target: resolveTarget(
        'delivered',
        firstTarget(hasDelivery && 'delivery', showEnd && 'end'),
      ),
      symbol: 'action',
      ...managerFields('delivered'),
    });
  }

  if (showEnd) {
    items.push({
      id: 'end',
      title: 'Fim do trajeto',
      time: formatRouteDateTime(timestamps.deliveredAt),
      target: 'end',
      symbol: 'end',
    });
  }

  if (timestamps.returnedAt) {
    items.push({
      id: 'returned',
      title: 'Retornou ao estabelecimento',
      time: formatRouteDateTime(timestamps.returnedAt),
      target: resolveTarget('returned', firstTarget(hasPlace && 'place')),
      symbol: 'action',
      ...managerFields('returned'),
    });
  }

  if (timestamps.finishedAt) {
    items.push({
      id: 'finished',
      title: 'Finalizou',
      time: formatRouteDateTime(timestamps.finishedAt),
      target: resolveTarget(
        'finished',
        firstTarget(showEnd && 'end', hasDelivery && 'delivery'),
      ),
      symbol: 'action',
      ...managerFields('finished'),
    });
  }

  return items;
};

export const hasDeliverymanRouteContent = (delivery: DeliveryRouteData) =>
  Boolean(delivery.encodedRoute) ||
  isValidLocation(delivery.localizacaoOrigem) ||
  isValidLocation(delivery.localizacaoDestino) ||
  Boolean(getLiveDeliverymanLocation(delivery)) ||
  getDeliveryActionPins(delivery).length > 0;

export const isGpsRouteRetentionExpired = (createdAt?: string) => {
  if (!createdAt) {
    return false;
  }

  const created = new Date(createdAt);

  if (Number.isNaN(created.getTime())) {
    return false;
  }

  return addDays(created, GPS_ROUTE_RETENTION_DAYS) <= new Date();
};

export const getGpsRouteUnavailableMessage = (delivery: DeliveryRouteData) => {
  if (hasGpsRouteOrLiveDelivery(delivery)) {
    return null;
  }

  if (isGpsRouteRetentionExpired(delivery.createdAt)) {
    return {
      title: 'Rota GPS não disponível',
      description: `O trajeto GPS é removido ${GPS_ROUTE_RETENTION_DAYS} dias após a criação da entrega.`,
    };
  }

  return {
    title: 'Rota GPS não disponível',
    description: 'O trajeto GPS desta entrega não foi registrado.',
  };
};
